import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import TextButton from 'Components/Molecules/TextButton';
import Icon from 'Components/Atoms/Icon/Icon';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import useCurrency from 'Hooks/useCurrency';
import useLocale from 'Hooks/useLocale';
import { formatCurrency } from 'Utils/formatters';
import { REPLAY_BLOCK_CLASS } from 'Utils/constants';
import { cn } from 'Utils/cn';

const HATCHED_BACKGROUND = [
    'repeating-linear-gradient(-45deg,',
    'color-mix(in srgb, var(--color-primary) 45%, #fff) 0 2px,',
    'color-mix(in srgb, var(--color-primary) 10%, #fff) 2px 5px)',
].join(' ');

function SummaryBullet({ variant }) {
    return (
        <span
            className={cn(
                'block size-3 rounded-full',
                variant === 'consolidated' && 'bg-primary',
                // A total, not a segment of the bar: an outline instead of the
                // colour the bar uses for what is left to reach the goal
                variant === 'forecasted' && 'border border-primary/40',
            )}
            style={variant === 'pending' ? { backgroundImage: HATCHED_BACKGROUND } : undefined}
        />
    );
}

SummaryBullet.propTypes = {
    variant: PropTypes.oneOf(['consolidated', 'pending', 'forecasted']).isRequired,
};

function SummaryCard(props) {
    const { items, className, currency, locale } = props;

    const label = items.map((item) => item.label).join(' / ');
    const amounts = items.map((item) => formatCurrency(item.amount ?? 0, { currency, locale }));

    return (
        <div className={cn('rounded-xl border border-gray-200 bg-white px-3 py-3 min-w-0', className)}>
            <div className="flex items-center gap-1 mb-4">
                {items.map((item) => (
                    <SummaryBullet key={item.variant} variant={item.variant} />
                ))}
            </div>

            <div className="text-2xs uppercase tracking-wide text-gray-500 truncate">{label}</div>

            <div
                className={cn('flex flex-wrap items-baseline gap-x-1 text-sm font-mono', REPLAY_BLOCK_CLASS)}
                title={amounts.join(' / ')}
            >
                {amounts.map((amount, index) => (
                    <Fragment key={items[index].variant}>
                        {index > 0 && <span className="text-gray-400">/</span>}
                        <span className="truncate min-w-0">{amount}</span>
                    </Fragment>
                ))}
            </div>
        </div>
    );
}

SummaryCard.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            variant: PropTypes.oneOf(['consolidated', 'pending', 'forecasted']).isRequired,
            amount: PropTypes.number,
        }),
    ).isRequired,
    className: PropTypes.string,
    currency: PropTypes.string,
    locale: PropTypes.string,
};

export default function ForecastValueSummary(props) {
    const { cell, onOpenGoal, onDeleteGoal, isDeletingGoal = false } = props;

    const { t } = useTranslation('forecasts');
    const currency = useCurrency();
    const locale = useLocale();

    const consolidated = cell?.amount_consolidated ?? 0;
    const pending = cell?.amount_pending ?? 0;
    const forecasted = cell?.amount_forecasted ?? 0;
    const goal = cell?.amount_goal ?? null;

    const total = Math.max(Math.abs(goal ?? 0), Math.abs(consolidated) + Math.abs(pending));
    const consolidatedWidth = total ? (Math.abs(consolidated) / total) * 100 : 0;
    const pendingWidth = total ? (Math.abs(pending) / total) * 100 : 0;

    return (
        <div className="mb-6 grid gap-2">
            <div className="grid grid-cols-3 gap-2">
                <SummaryCard
                    items={[{ label: t('cell.consolidated'), amount: consolidated, variant: 'consolidated' }]}
                    currency={currency}
                    locale={locale}
                />
                <SummaryCard
                    className="col-span-2"
                    items={[
                        { label: t('cell.pending'), amount: pending, variant: 'pending' },
                        { label: t('cell.expected'), amount: forecasted, variant: 'forecasted' },
                    ]}
                    currency={currency}
                    locale={locale}
                />
            </div>

            <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
                <span className="text-2xs uppercase tracking-wide text-gray-500">{t('cell.goal')}</span>

                {goal === null ? (
                    <TextButton color="black" className="inline-flex items-center gap-1" onClick={onOpenGoal}>
                        <Icon name="plus" size={16} color="currentColor" />
                        <span className="small">{t('cell.set-goal')}</span>
                    </TextButton>
                ) : (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className={cn('font-mono hover:underline', REPLAY_BLOCK_CLASS)}
                            onClick={onOpenGoal}
                            title={t('cell.edit-goal')}
                        >
                            {formatCurrency(goal, { currency, locale })}
                        </button>

                        {onDeleteGoal && (
                            <IconButton
                                variant="ghost"
                                size="small"
                                iconSize={18}
                                tooltip={t('cell.delete-goal')}
                                icon={<Icon name="trash" />}
                                onClick={onDeleteGoal}
                                disabled={isDeletingGoal}
                            />
                        )}
                    </div>
                )}
            </div>

            {total > 0 && (
                <div className="flex h-3 overflow-hidden rounded-full bg-primary/10">
                    <span className="bg-primary" style={{ width: `${consolidatedWidth}%` }} />
                    <span style={{ width: `${pendingWidth}%`, backgroundImage: HATCHED_BACKGROUND }} />
                </div>
            )}
        </div>
    );
}

ForecastValueSummary.propTypes = {
    onOpenGoal: PropTypes.func.isRequired,
    cell: PropTypes.object,
    onDeleteGoal: PropTypes.func,
    isDeletingGoal: PropTypes.bool,
};
